/**
 * Picture-and-text blocks in article Markdown:
 *
 *   :::bild-text links
 *   ![Beschreibung](/media/ab/ab12....png)
 *
 *   Text beside the picture, with **any** Markdown ...
 *   :::
 *
 * The word after "bild-text" places the picture: "links" (left of the text),
 * "rechts" (right) or "oben" (above); "media-text" and "left"/"right"/"top"
 * work as well. The picture is the first line of the block; a block without
 * one keeps an empty picture area. Other ":::" blocks may be nested in the
 * text; fenced code blocks are left alone.
 */

const OPEN = /^\s*:::\s*(?:bild-text|media-text)(?:\s+(\S+))?\s*$/i;
const NESTED_OPEN = /^\s*:::\s*\S/;
const CLOSE = /^\s*:::\s*$/;
const FENCE = /^\s*(```|~~~)/;
const IMAGE_LINE = /^\s*!\[([^\]]*)\]\(\s*([^)\s]+)(?:\s+"[^"]*")?\s*\)\s*$/;

export const MEDIA_TEXT_LAYOUTS = ['left', 'right', 'top'];

const LAYOUT_OF = {
  links: 'left',
  left: 'left',
  rechts: 'right',
  right: 'right',
  oben: 'top',
  top: 'top',
};

const WORD_OF = { left: 'links', right: 'rechts', top: 'oben' };

/** The layout an opening line asks for, or null when it does not open a picture-and-text block. */
export function mediaTextLayout(line) {
  const match = line.match(OPEN);
  if (!match) return null;

  return LAYOUT_OF[(match[1] ?? '').toLowerCase()] ?? 'left';
}

/** The opening line for a layout, as the editor writes it. */
export function mediaTextOpening(layout) {
  return `:::bild-text ${WORD_OF[layout] ?? WORD_OF.left}`;
}

/** A block's body lines as { image: { alt, src } | null, text }. */
export function parseMediaTextBody(lines) {
  const first = lines.findIndex((line) => line.trim() !== '');
  const match = first >= 0 ? lines[first].match(IMAGE_LINE) : null;

  if (!match) return { image: null, text: lines.join('\n').trim() };

  return {
    image: { alt: match[1].trim(), src: match[2] },
    text: lines.slice(first + 1).join('\n').trim(),
  };
}

/** Splits Markdown into { type: 'markdown', text } and { type: 'mediaText', layout, image, text }. */
export function splitMediaTextBlocks(markdown = '') {
  const lines = markdown.split(/\r?\n/);
  const segments = [];
  let buffer = [];
  let block = null;
  let depth = 0;
  let inFence = false;

  function flushText() {
    if (buffer.length) {
      segments.push({ type: 'markdown', text: buffer.join('\n') });
      buffer = [];
    }
  }

  for (const line of lines) {
    if (FENCE.test(line)) inFence = !inFence;

    if (!block) {
      const layout = inFence ? null : mediaTextLayout(line);

      if (layout) {
        block = { layout, lines: [], opening: line };
        depth = 1;
      } else {
        buffer.push(line);
      }
      continue;
    }

    if (!inFence && CLOSE.test(line)) depth -= 1;
    else if (!inFence && NESTED_OPEN.test(line)) depth += 1;

    if (depth > 0) {
      block.lines.push(line);
      continue;
    }

    flushText();
    segments.push({ type: 'mediaText', layout: block.layout, ...parseMediaTextBody(block.lines) });
    block = null;
  }

  // An unclosed block is just text.
  if (block) buffer.push(block.opening, ...block.lines);

  flushText();
  return segments;
}
